import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
    createAutoImport,
    updateAutoImport,
    getAutoImport,
    getImportTypeData,
    getImportFileTypeData,
    getAllImportTemplatesForTenant,
    getAutoImportFolders
} from "../../services/importService";
import { Dropdown } from "../helpers/Dropdown";
import { TextInput } from "../helpers/TextInput";
import Toast from "../ui/toasts/Toast";
import { ModalContext, ModalTypes } from "../../store/ModalContext";
import Spinner from "../ui/spinners/Spinner";
import { useAuthContext } from "../../store/Contexts";
import { SecurityAttributes } from "../../utils/data/enums";
import styles from "./ImportQueuePage.module.scss";
import SSOImportVendorDropdown from "../shared/asyncDropdowns/SSOImportVendorDropdown";
import { isNumber } from "highcharts";
import { ROUTES } from '../../constants/routes';
import { ImportType, TenantTiers } from "../../utils/data/enums";
import { getText } from '../../i18n';

const EditAutoImport = () => {
    const errorThresholdPercentageMin = 1;
    const errorThresholdPercentageMax = 100;

    const newAutoImport = {
        id: 0,
        tenantId: window.localStorage.getItem("tenantId"),
        name: '',
        importTypeId: -1,
        fileName: '',
        isActive: true,
        importTemplateId: -1,
        isTest: false,
        errorThresholdPercentage: errorThresholdPercentageMax,
        importFileTypeId: -1,
        autoImportFolderId: -1,
        vendorId: -1,
    };

    const { showModal } = useContext(ModalContext);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();
    const location = useLocation();
    const { autoImportId } = useParams();

    const [autoImport, setAutoImport] = useState(newAutoImport);

    const [errorThresholdPercentages, setErrorThresholdPercentages] = useState([]);
    const [isFormDirty, setIsFormDirty] = useState(false);
    const [categories, setCategories] = useState([]);
    const [processFormats, setProcessFormats] = useState([]);
    const [customTemplates, setCustomTemplates] = useState([]);
    const [standardTemplates, setStandardTemplates] = useState([]);
    const { user, isSelfRegistrationEnabled, tenantTierId, hasSecurityAttribute } = useAuthContext();
    const [typedTemplates, setTypedTemplates] = useState([]);
    const [autoImportFolders, setAutoImportFolders] = useState([]);

    const isNewAutoImport = location.pathname.includes("add");

    const selectOptionDropDownValue = {
        value: -1,
        label: getText('msg_7a7d6f0274ce'),
    }

    useEffect(() => {
        const loadDetails = async () => {
            try {
                setIsLoading(true);

                let loadedAutoImport;

                if (!isNewAutoImport && autoImportId) {
                    loadedAutoImport = await getAutoImport(parseInt(autoImportId));
                    setAutoImport(loadedAutoImport);
                }
                else {
                    loadedAutoImport = newAutoImport
                }

                await getImportTypeData()
                    .then(function (response) {
                        let newCategories = [];
                        let templates = [];

                        var allImportTypes = response.map(item => ({ value: item.id, label: item.name }));

                        const shouldFilterImportTypes = isSelfRegistrationEnabled || tenantTierId === TenantTiers.Base;

                        if (shouldFilterImportTypes) {
                            const filteredImportTypes = allImportTypes.filter(item =>
                                item.value !== ImportType.Demographic
                                && item.value !== ImportType.Enrollment
                            );
                            newCategories = [selectOptionDropDownValue, ...filteredImportTypes];
                            templates = filteredImportTypes.map(importType => ({ id: 0, importTypeId: importType.value, name: "Standard " + importType.label }));
                        }
                        else {
                            newCategories = [selectOptionDropDownValue, ...allImportTypes];
                            templates = allImportTypes.map(importType => ({ id: 0, importTypeId: importType.value, name: "Standard " + importType.label }));
                        }

                        setCategories(newCategories);
                        setStandardTemplates(templates);
                    })
                    .catch(function (error) {
                        console.error(error);
                    });

                await getImportFileTypeData()
                    .then(function (response) {
                        let newProcessFormats = [];
                        newProcessFormats.push(selectOptionDropDownValue);
                        for (let i = 0; i < response.length; i++) {
                            const item = response[i];
                            const newItem = {
                                value: item.id,
                                label: item.name,
                            };
                            newProcessFormats.push(newItem);
                        }
                        setProcessFormats(newProcessFormats);
                    })
                    .catch(function (error) {
                        console.error(error);
                    });

                let newErrorThresholdPercentages = [];
                for (
                    let i = errorThresholdPercentageMax;
                    i >= errorThresholdPercentageMin;
                    i--
                ) {
                    const newErrorThresholdPercentage = {
                        value: i,
                        label: i,
                    };
                    newErrorThresholdPercentages.push(newErrorThresholdPercentage);
                }
                setErrorThresholdPercentages(newErrorThresholdPercentages);

                await getAllImportTemplatesForTenant().then(function (customTemplates) {

                    let templates = [];
                    for (let i = 0; i < customTemplates.length; i++) {
                        const template = customTemplates[i];
                        if (template.importTypeIds.length > 1) {
                            // append "Multi Import" to name
                            template.name += ' — ' + getText('msg_09cb6197bf14');
                        }
                        templates.push(template);
                    }
                    setCustomTemplates(templates);
                })
                    .catch(function (error) {
                        console.error(error);
                    });

                await getAutoImportFolders()
                    .then(function (response) {
                        let folders = [];
                        folders.push(selectOptionDropDownValue);
                        for (let i = 0; i < response.length; i++) {
                            const item = response[i];
                            const newItem = {
                                value: item.id,
                                label: item.path,
                            };
                            folders.push(newItem);
                        }
                        setAutoImportFolders(folders);
                    })
                    .catch(function (error) {
                        console.error(error);
                    });

                setAutoImport(loadedAutoImport);
            } catch (error) {
                console.error("Failed to load data", error);
                Toast.error(getText('msg_3bcb8c740c1f'));
            } finally {
                setIsLoading(false);
            }
        };
        loadDetails();
    }, [autoImportId]);

    useEffect(() => {
        loadTemplatesForImportTypeType(autoImport.importTypeId);
    }, [customTemplates, standardTemplates]);

    const handleCategoryChange = (value) => {
        if (parseInt(value) >= 0) {
            handleChange({ target: { name: 'importTypeId', value: value } })
            handleChange({ target: { name: 'importTemplateId', value: -1 } }) // default to the select option
            loadTemplatesForImportTypeType(value);
        }
        else {
            handleChange({ target: { name: 'importTypeId', value: -1 } })
            handleChange({ target: { name: 'importTemplateId', value: -1 } })
            setTypedTemplates([]);
        }
    };

    const loadTemplatesForImportTypeType = (importTypeId) => {

        let allTemplates = [];

        // add the standard template
        allTemplates.push(...standardTemplates.filter((template) => template.importTypeId === importTypeId));

        // add the custom templates
        allTemplates.push(...customTemplates.filter(template =>
            template.importTypeIds.some(typeId => typeId === importTypeId)));

        const sortedTemplates = allTemplates.sort(function (a, b) {
            return a.name.localeCompare(b.name);
        });

        // the "Select Option" and the templates
        const templatesForDropDown = [selectOptionDropDownValue, ...sortedTemplates.map((template) => { return { value: template.id, label: template.name } })];

        setTypedTemplates(templatesForDropDown);

        return templatesForDropDown;
    }

    const handleVendorChange = (value) => {
        if (isNumber(value))
            handleChange({ target: { name: 'vendorId', value: value } })
    }

    const handleLayoutChange = (value) => {
        if (isNumber(value))
            handleChange({ target: { name: 'importTemplateId', value: value } })
    };

    const handleProcessFormatChange = (value) => {
        if (isNumber(value))
            handleChange({ target: { name: 'importFileTypeId', value: value } });
    }

    const handleErrorThresholdPercentageChange = (value) => {
        handleChange({ target: { name: 'errorThresholdPercentage', value: value } })
    }

    const handleFolderChange = (value) => {
        handleChange({ target: { name: 'autoImportFolderId', value: value } })
    }

    const handleFileNameChange = (value) => {
        handleChange({ target: { name: 'fileName', value: value } })
    }

    const handleTestChange = (value) => {
        handleChange({ target: { name: 'isTest', value: value } })
    }

    const handleNameChange = (value) => {
        handleChange({ target: { name: 'name', value: value } })
    }

    const handleChange = (e) => {
        const { name, value } = e.target;
        setAutoImport((autoImport) => ({ ...autoImport, [name]: value }));
        setIsFormDirty(true);
    };

    const validate = () => {

        if (autoImport.name.length === 0)
            return "Name is required";

        if (autoImport.importTypeId <= 0)
            return "Category is required";

        if (autoImport.importTemplateId < 0)
            return "Layout is required";

        if (autoImport.importFileTypeId <= 0)
            return "Process Format is required";

        if (autoImport.errorThresholdPercentage <= 0)
            return "Error threshold percentage is required";

        if (autoImport.fileName.length === 0)
            return "File Name is required";

        if (autoImport.autoImportFolderId <= 0)
            return "Folder is required";

        if (autoImport.vendorId <= 0)
            return "Vendor is required";

        return null;
    }

    const handleSave = async () => {

        if (!isFormDirty) {
            Toast.warning(getText('msg_13c3a7ac4e47'));
            return;
        }

        const errorMessage = validate();

        if (errorMessage) {
            Toast.error(errorMessage);
            return;
        }

        try {
            let response;

            if (isNewAutoImport)
                response = await createAutoImport(autoImport)
            else
                response = await updateAutoImport(autoImport);

            var httpStatusCode = response?.status;

            if (httpStatusCode === 400) {
                var message = response.message;
                Toast.error(message);
                return;
            } else if (httpStatusCode === 500) {
                const action = isNewAutoImport ? "add" : "update";
                Toast.error(`An error occured when attempting to ${action} the auto import`);
                return;
            }

            Toast.success(getText('msg_7102952534f9'));
            returnToList();

        } catch (error) {
            const action = isNewAutoImport ? "add" : "update";
            Toast.error(`An error occured when attempting to ${action} the auto import`);
            console.error(`Failed to ${action} the auto import:`, error);
        }
    };

    const returnToList = () => {
        navigate(ROUTES.AUTO_IMPORTS);
    };

    const handleClose = () => {

        if (!isFormDirty) {
            returnToList();
            return;
        }
        showModal({
            type: ModalTypes.ACTION,
            title: getText('msg_b78130c92f32'),
            subtitle: getText('msg_2f4e8fc9ac09'),
            text: getText('msg_f3cefc556397'),
            onYes: returnToList,
            onNo: () => { },
            noButtonText: getText('msg_816c52fd2bdd'),
            yesButtonText: getText('msg_5397e0583f14'),
        });
    };

    const getImportTypeValue = () => {
        const match = categories.find((item) => item.value === autoImport.importTypeId);
        return match?.label ?? selectOptionDropDownValue.label;
    };

    const getProcessFormatValue = () => {
        const match = processFormats.find((item) => item.value === autoImport.importFileTypeId);
        return match?.label ?? selectOptionDropDownValue.label;
    };

    const getTemplateValue = () => {
        const match = typedTemplates.find((item) => item.value === autoImport.importTemplateId);
        return match?.label ?? selectOptionDropDownValue.label;
    };

    const getFolderValue = () => {
        const match = autoImportFolders.find((item) => item.value === autoImport.autoImportFolderId);
        return match?.label ?? selectOptionDropDownValue.label;
    };

    return (
        <div className={`page-content`}>
            <div className="card">
                <div className="card-body px-0 py-4 b">
                    <div className="d-flex justify-content-between align-items-center pb-3 border-bottom">
                        <p className="page-title float-start card-title ms-4 mb-0">
                            {isNewAutoImport
                                ? "Add New Auto Import"
                                : `Edit Auto Import: ${autoImport.name}`}
                        </p>

                        <div className="float-end d-flex align-items-center">
                            <span
                                type="button"
                                onClick={handleClose}
                                className="ing-text-buttons mx-4"
                            >
                                <i className="bi bi-x me-2" />
                                {getText('msg_bbfa773e5a63')}
                            </span>
                        </div>
                    </div>

                    {isLoading ? (
                        <Spinner width={50} className={styles.loadingSpinner} />
                    ) : (
                        <form
                            onSubmit={(e) => e.preventDefault()}
                            className={`m-auto col-xs-12 col-sm-12 col-md-10 col-lg-8 px-4 py-5`}
                        >
                            <section className="card-title d-flex flex-wrap mb-5 overflow-visible flex-column">
                                <div className={`form-group col-12 col-sm-12 col-md-12 col-lg-12 mb-4`}>
                                    <div className="file-form-label">{getText('msg_709a23220f2c')}<span className="text-danger">*</span></div>
                                    <TextInput OnChange={handleNameChange} Value={autoImport.name} CharacterLimit={128} />
                                </div>
                                <div className={`form-group col-12 col-sm-12 col-md-12 col-lg-12 mb-4`}>
                                    <SSOImportVendorDropdown
                                        selectedVendor={autoImport.vendorId}
                                        onVendorChange={handleVendorChange}
                                        inlineLabel={false}
                                        isRequired={true}
                                        label={getText('msg_d96159ff30af')}
                                        placeholder={getText('msg_1b5e74225da1')}
                                        className=""
                                    />
                                </div>
                                <div
                                    className={`form-group col-12 col-sm-12 col-md-12 col-lg-12 mb-4`}
                                >
                                    <div className="file-form-label">{getText('msg_a3c686e711e4')}<span className="text-danger">*</span></div>
                                    <Dropdown
                                        defaultValue={getImportTypeValue()}
                                        options={categories}
                                        onChange={handleCategoryChange}
                                    />
                                </div>
                                <div
                                    className={`form-group col-12 col-sm-12 col-md-12 col-lg-12 mb-4`}
                                >
                                    <div className="file-form-label">{getText('msg_972ad8d8a503')}<span className="text-danger">*</span></div>
                                    <Dropdown
                                        defaultValue={getTemplateValue()}
                                        options={typedTemplates}
                                        onChange={handleLayoutChange}
                                        disabled={autoImport.importTypeId <= 0}
                                    />
                                </div>
                                <div
                                    className={`form-group col-12 col-sm-12 col-md-12 col-lg-12 mb-4`}
                                >
                                    <div className="file-form-label">{getText('msg_12c8f2c6b412')}<span className="text-danger">*</span></div>
                                    <Dropdown
                                        defaultValue={getProcessFormatValue()}
                                        options={processFormats}
                                        onChange={handleProcessFormatChange}
                                    />
                                </div>
                                <div className={`form-group col-12 col-sm-12 col-md-12 col-lg-12 mb-4`}>
                                    <div className="file-form-label">{getText('msg_86d19e1c5d48')}<span className="text-danger">*</span></div>
                                    <Dropdown
                                        defaultValue={autoImport.errorThresholdPercentage}
                                        options={errorThresholdPercentages}
                                        onChange={handleErrorThresholdPercentageChange}
                                    />
                                </div>
                                <div className={`form-group col-12 col-sm-12 col-md-12 col-lg-12 mb-4`}>
                                    <div className="file-form-label">{getText('msg_30baa24967e0')}<span className="text-danger">*</span></div>
                                    <Dropdown
                                        defaultValue={getFolderValue()}
                                        options={autoImportFolders}
                                        onChange={handleFolderChange}
                                    />
                                </div>
                                <div className={`form-group col-12 col-sm-12 col-md-12 col-lg-12 mb-4`}>
                                    <div className="file-form-label">{getText('msg_2aabcfc2668a')}<span className="text-danger">*</span></div>
                                    <TextInput OnChange={handleFileNameChange} Value={autoImport.fileName} CharacterLimit={128} />
                                </div>
                                <div className="form-checkbox-container">
                                    <input
                                        type="checkbox"
                                        checked={autoImport.isTest}
                                        onChange={(e) => handleTestChange(e.target.checked)}
                                        className="form-check-input custom-checkbox"
                                    />
                                    {getText('msg_640ab2bae07b')}<span className="text-danger">*</span>
                                </div>
                                <div className="d-flex justify-content-between">
                                    <p>
                                        <span className="text-danger">*</span> {getText('msg_7c118f042eaa')}
                                    </p>
                                    {hasSecurityAttribute(
                                        [SecurityAttributes.ImportsAutoImport]
                                    ) && (
                                            <span
                                                onClick={handleSave}
                                                type="button"
                                                className="ms-3 button-base ing-button-primary-left align-middle"
                                            >
                                                <i className="bi bi-check-lg me-2" />
                                                {getText('msg_efc007a393f6')}
                                            </span>
                                        )}
                                </div>
                            </section>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EditAutoImport;
